/**
 * Live subscriptions for the scheduledJobs admin UI.
 *
 *   - `useScheduledJobs(pid)` — the per-project job catalog (one doc
 *     per `ScheduledJobId`). Powers the Settings page section that
 *     lets admins toggle / re-cron / kick each job.
 *   - `useScheduledJobRuns(pid, jobId, max)` — paginated history view.
 *     Sorted desc by `startedAt` so the freshest run stays on top.
 *
 * Both queries are gated by `memberUserIds array-contains <uid>` so a
 * non-member subscribing to the collection group sees nothing — the
 * Firestore rules enforce the same on the server side.
 */
import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type Query,
} from 'firebase/firestore';
import type {
  ScheduledJobDoc,
  ScheduledJobRunDoc,
} from '@mct/shared';
import { getDb } from '../lib/firebase';
import { useAuthState } from './useAuth';

// `useCollection`-style local helper kept private to this module so
// the file is self-contained and the import surface stays small.
function useLiveCollection<T>(qOrNull: Query<DocumentData> | null) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!qOrNull) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      qOrNull,
      (snap) => {
        setData(snap.docs.map((d) => d.data() as T));
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [qOrNull]);
  return { data, loading, error };
}

export function useScheduledJobs(projectId: string | null) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !uid) {
      setQ(null);
      return;
    }
    // Intentionally no `orderBy` on the server side: combining
    // `array-contains` with an ordered field on a different key requires
    // a composite index, and the UI already re-sorts client-side
    // (ScheduledJobsSection sorts by `jobId`). Adding orderBy here
    // silently 0-rows the snapshot on any project whose index hasn't
    // been provisioned.
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'scheduledJobs'),
        where('memberUserIds', 'array-contains', uid),
      ),
    );
  }, [projectId, uid]);
  return useLiveCollection<ScheduledJobDoc>(q);
}

export function useScheduledJobRuns(
  projectId: string | null,
  jobId: string | null,
  max = 100,
) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !jobId || !uid) {
      setQ(null);
      return;
    }
    // Same composite-index constraint as `useScheduledJobs`: we re-sort
    // client-side after the snapshot lands so server-side `orderBy` (which
    // would otherwise need a composite index) is skipped.
    setQ(
      query(
        collection(
          getDb(),
          'projects',
          projectId,
          'scheduledJobs',
          jobId,
          'runs',
        ),
        where('memberUserIds', 'array-contains', uid),
      ),
    );
  }, [projectId, jobId, uid]);
  const r = useLiveCollection<ScheduledJobRunDoc>(q);
  const sorted = [...r.data].sort((a, b) => {
    const at = a.startedAt?.toMillis?.() ?? 0;
    const bt = b.startedAt?.toMillis?.() ?? 0;
    return bt - at;
  });
  return { ...r, data: sorted.slice(0, max) };
}
